import axios from 'axios';
import { getStoredParentStudentId, clearStoredParentStudentId } from '../utilities/teacherModulePermissions';

// Every API call funnels through here, so this is the single place that scopes
// requests to whichever child a parent (user_type 3) account currently has
// selected — callers never need to remember to add student_id themselves, and
// it can't be silently dropped by some file that forgot to thread it through.
function withParentStudentId(params, path = '') {
  const studentId = getStoredParentStudentId();
  if (!studentId) {
    return params;
  }

  // "public/" endpoints are organization-wide config/listings (general
  // settings — which is where the parent handbook URL comes from — CMS
  // pages, blog, public event lists, ...), never scoped to one child.
  // Auto-attaching student_id here doesn't apply, and made the parent
  // handbook button silently stop showing for parent/student accounts:
  // it was reaching general-settings with an unexpected student_id param
  // the backend doesn't handle for that endpoint.
  if (path.startsWith('public/')) {
    return params;
  }

  if (params instanceof FormData) {
    if (!params.has('student_id')) {
      params.append('student_id', studentId);
    }
    return params;
  }

  if (params && typeof params === 'object' && !Array.isArray(params)) {
    if (params.student_id === undefined || params.student_id === null || params.student_id === '') {
      return { ...params, student_id: studentId };
    }
  }

  return params;
}

// True when a request never actually reached the server at all - no wifi/data,
// DNS failure, the API host unreachable, etc. - as opposed to the server
// answering with an error status. Axios gives a network failure no
// `.response` (there was no HTTP response to have) and either
// `code: 'ERR_NETWORK'` (modern axios) or `message: 'Network Error'` (older
// axios / some browsers) - checking both covers the versions this app has
// run against. `navigator.onLine === false` is included too so a request
// that was already in flight when the browser went offline is still caught,
// even on an axios version/browser combo that reports neither of the above.
function isNetworkError(res) {
  return (
    !res?.response &&
    (res?.code === 'ERR_NETWORK' ||
      res?.message === 'Network Error' ||
      (typeof navigator !== 'undefined' && navigator.onLine === false))
  );
}

// Pulls the backend's actual error message out of an apiHandler() result,
// whichever of the shapes it comes back in.
//
// Every auth page reads `res?.response?.data?.data?.message` directly, which
// only exists when apiHandler's catch(e) returned a raw axios error (i.e. the
// request failed with a non-2xx HTTP status - the "Account not found" /
// "Need Admin Approval" style responses this API returns for login failures
// are of this shape). If the backend ever answers with HTTP 200 but an
// embedded failure code instead (the pattern every caller's success check,
// `res.data?.response?.status === 200`, is explicitly guarding against),
// that path resolves to `undefined` and the user sees a blank toast instead
// of whatever the API actually said. Checking every shape means the real
// message always surfaces.
//
// A true network failure (see isNetworkError above) is checked first and
// given its own friendly copy - left to fall through to the generic checks
// below, it would otherwise surface axios's raw `"Network Error"` string,
// which reads like a stack trace rather than something a user can act on.
// The global NetworkBanner (App.js) already flags this app-wide; this is the
// same information surfacing in whatever toast/alert the calling screen was
// already going to show for this one failed action.
//
// "No internet" is only claimed when the browser itself reports being
// offline. A request can also come back with no readable response while the
// device is perfectly online - the API host is down/unreachable, the request
// timed out, or the browser hid an error reply the server sent without CORS
// headers. Telling a user who just typed a wrong password that their
// internet is down (the reported bug) sends them chasing the wrong problem,
// so that case gets its own "can't reach the server" copy instead.
//
// When the server did answer but without a message, a plain-language
// fallback per status code beats a generic "Something went wrong".
const STATUS_MESSAGES = {
  400: "Please check the details you entered and try again.",
  401: "Incorrect email or password. Please try again.",
  403: "You don't have permission to do that.",
  404: "Account not found. Please check your email and try again.",
  408: "The request took too long. Please try again.",
  422: "Please fill in all the required fields correctly.",
  429: "Too many attempts. Please wait a minute and try again.",
};

function getApiErrorMessage(res, fallback = "Something went wrong. Please try again.") {
  const serverMessage =
    res?.response?.data?.data?.message ||
    res?.response?.data?.message ||
    res?.data?.data?.message ||
    res?.data?.response?.message;
  // A real message from the API always wins ("Invalid password.",
  // "Account not found.", "Need Admin Approval", ...).
  if (typeof serverMessage === "string" && serverMessage.trim()) {
    return serverMessage;
  }

  const status = res?.response?.status;
  if (status) {
    if (STATUS_MESSAGES[status]) return STATUS_MESSAGES[status];
    if (status >= 500) return "The server had a problem. Please try again in a moment.";
    return fallback;
  }

  if (isNetworkError(res) || res?.code === 'ECONNABORTED') {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      return "No internet connection. Please check your network and try again.";
    }
    return "We couldn't reach the server. Please try again in a moment.";
  }

  // Never surface axios internals like "Request failed with status code 401".
  if (typeof res?.message === "string" && res.message && !/^Request failed with status code/i.test(res.message)) {
    return res.message;
  }
  return fallback;
}

// The API answers a bad/expired session JWT with HTTP 422 + "Invalid token"
// (JwtService.verify - see start/socket.ts's comment on the same check).
// Distinct from DownloadFileMiddleware's own "Invalid token" message (403,
// a download-link token, nothing to do with the logged-in session) - gating
// on the 422 pairs with that message so only an actually-dead session
// forces a sign-out, never a failed file download.
function isInvalidSessionToken(e) {
  return e?.response?.status === 422 && e?.response?.data?.data?.message === 'Invalid token';
}

// Every caller's session is unrecoverable once this fires - the JWT itself
// (not just this one request) is bad, so the same failure would repeat on
// every subsequent call. Clear the stale session and send them to the
// homepage, same as a manual sign-out (see header.js's Sign out handler),
// via a full navigation rather than react-router's navigate() - this is a
// plain module, not a component, so there's no router/dispatch to call.
function redirectToHomeOnInvalidToken(e) {
  if (!isInvalidSessionToken(e)) {
    return;
  }
  localStorage.removeItem('uzoneAuthToken');
  clearStoredParentStudentId();
  window.location.href = '/';
}

async function apiHandler(path, method = "GET", params = {}, headers = {}) {
  if (localStorage.getItem("uzoneAuthToken")) {
    //var userData= JSON.parse(localStorage.getItem("uzoneAuthToken"))
    headers = {
      'x-auth-token': `${localStorage.getItem("uzoneAuthToken")}`,
    };
  }
  params = withParentStudentId(params, path);
  let apiRes = null
  const baseURL = 
  process.env.REACT_APP_API_BASE_URL || "https://api.harmonydaycare.ca/";
// "https://daycare-api.syntch.ai/";
  if (!baseURL) {
    return {
      data: {
        response: {
          status: 500,
          message: "Missing REACT_APP_API_BASE_URL in environment",
        },
      },
    };
  }
  switch (method) {
    case "GET":
      try {
        //return await axios.get(`${process.env.API_URL}${path}`, {
        apiRes = await axios.get(`${baseURL}${path}`, {
          headers: headers,
        })
        return apiRes
      }
      catch (e) {
        redirectToHomeOnInvalidToken(e)
        return e
      }

    case "GET1":
      try {
        //console.log("URL console",process.env.API_URL)
        apiRes = await axios.get(`${baseURL}${path}`, {
          headers: headers,
          params: params
        })
        return apiRes
      }
      catch (e) {
        redirectToHomeOnInvalidToken(e)
        return e
      }

    case "POST":
      try {
        //console.log("URL console",process.env.API_URL)
        apiRes = await axios.post(`${baseURL}${path}`, params, {
          headers: headers,
        })
        return apiRes
      }
      catch (e) {
        redirectToHomeOnInvalidToken(e)
        return e
      }

    case "POSTFILE":
      try {
        //console.log("URL console",process.env.API_URL)
        apiRes = await axios.post(`${baseURL}${path}`, params, {
          headers: headers,
        })
        return apiRes
      }
      catch (e) {
        redirectToHomeOnInvalidToken(e)
        return e
      }

    case "DELETE":
      try {
        //console.log("URL console",process.env.API_URL)
        apiRes = await axios.delete(`${baseURL}${path}`, {
          headers: headers,
          data: params
        })
        return apiRes
      }
      catch (e) {
        redirectToHomeOnInvalidToken(e)
        return e
      }

    default:
  }

}

export { apiHandler, getApiErrorMessage, isNetworkError };